import { FC, useMemo } from 'react'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import {
  ArrowBack,
  CalendarMonthOutlined,
  CampaignOutlined,
  PersonOutline,
  ShareOutlined
} from '@mui/icons-material'
import { useBoardsList, useBoardsListPosts } from 'services/boards/boards.hooks'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

// Board post detail ("Panel Klienta 3.0" Komunikaty — Szczegóły komunikatu).
const BoardsPost: FC = () => {
  const { formatMessage } = useIntl()
  const { boardId, id } = useParams()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const { data = [] } = useBoardsList()
  const { boardsListPosts } = useBoardsListPosts(data.map(({ id }) => id))

  const post = useMemo(
    () =>
      boardsListPosts.find((post) => post.id === Number(id) && post.board.id === Number(boardId)),
    [boardId, id, boardsListPosts]
  )

  if (!post) return null

  const title =
    post.title ??
    formatMessage({ id: 'boardsPost.event', defaultMessage: 'Wydarzenie {id}' }, { id })
  const coverImage = post.multimedia[0]

  const handleShare = () => {
    const shareData = { title, url: window.location.href }
    if (navigator.share) {
      navigator.share(shareData).catch(() => undefined)
    } else {
      navigator.clipboard.writeText(window.location.href)
    }
  }

  const attachments = !!post.multimedia.length && (
    <Box
      sx={{
        mt: '20px',
        pt: '16px',
        borderTop: `1px solid ${theme.r4r.greyLight}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%'
      }}>
      {post.multimedia.map((file: any) => (
        <Link
          key={file.id}
          href={file.multimedia}
          underline='none'
          display='flex'
          alignItems='center'
          target='_blank'
          sx={{ color: theme.r4r.azure }}>
          <AttachFileIcon sx={{ mr: 1, fontSize: '18px' }} />
          <Typography sx={{ wordBreak: 'break-all', fontSize: '13px', fontWeight: 600 }}>
            {file.multimedia.split('?')[0].substring(file.multimedia.lastIndexOf('/') + 1)}
          </Typography>
        </Link>
      ))}
    </Box>
  )

  if (isDesktop) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
          <IconButton
            onClick={() => navigate('/board')}
            sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
            <ArrowBack sx={{ fontSize: '22px' }} />
          </IconButton>
          <Typography
            sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
            {formatMessage({ id: 'boardsPost.title', defaultMessage: 'Komunikat' })}
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: '#fff', boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)' }}>
          <Box
            sx={{
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              background: coverImage
                ? undefined
                : 'linear-gradient(135deg, #7F71D3 0%, #6A8AE5 100%)'
            }}>
            {coverImage ? (
              <Box
                component='img'
                src={coverImage.multimedia}
                alt=''
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <CampaignOutlined sx={{ fontSize: '56px', color: alpha('#fff', 0.5) }} />
            )}
          </Box>
          <Box
            sx={{
              padding: '32px 36px 48px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
            <Box
              sx={{
                padding: '4px 12px',
                backgroundColor: alpha(theme.r4r.azure, 0.15),
                color: theme.r4r.azure,
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: 'normal'
              }}>
              {post.board.name}
            </Box>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 800,
                lineHeight: '31.2px',
                color: theme.r4r.navy
              }}>
              {title}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                width: '100%',
                pb: '21px',
                borderBottom: `1px solid ${theme.r4r.greyLight}`
              }}>
              {!!post.createdBy.firstName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <PersonOutline sx={{ fontSize: '16px', color: theme.r4r.grey }} />
                  <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
                    {post.createdBy.firstName}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <CalendarMonthOutlined sx={{ fontSize: '16px', color: theme.r4r.grey }} />
                <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
                  {dayjs(post.eventDate).format('D MMMM YYYY')}
                </Typography>
              </Box>
            </Box>
            <Typography
              component='div'
              sx={{
                fontSize: '15px',
                lineHeight: '27px',
                color: theme.r4r.ink,
                pt: '16px',
                width: '100%',
                '& p': { margin: '0 0 16px' },
                '& p:last-of-type': { marginBottom: 0 }
              }}
              dangerouslySetInnerHTML={{ __html: post.text }}
            />
            {attachments}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '12px',
              padding: '25px 36px 24px',
              borderTop: `1px solid ${theme.r4r.greyLight}`
            }}>
            <Button
              onClick={handleShare}
              startIcon={<ShareOutlined sx={{ fontSize: '16px' }} />}
              sx={{
                border: `1px solid ${theme.r4r.greyLight}`,
                borderRadius: 0,
                padding: '11px 21px',
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '13px',
                color: theme.r4r.grey,
                backgroundColor: '#fff',
                ':hover': { backgroundColor: '#F9FAFB' }
              }}>
              {formatMessage({ id: 'boardsPost.share', defaultMessage: 'Udostępnij' })}
            </Button>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <>
      <BackNavigationHeader navigateTo='/board' label={title} />
      <BackNavigationContent>
        {!!coverImage && (
          <Box
            component='img'
            src={coverImage.multimedia}
            alt=''
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              objectFit: 'cover',
              mb: '20px'
            }}
          />
        )}
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: theme.r4r.navy }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mt: '4px' }}>
          {dayjs(post.eventDate).format('DD/MM/YYYY HH:mm')}
          {post.createdBy.firstName ? ` • ${post.createdBy.firstName}` : ''}
        </Typography>
        <Typography
          component='div'
          sx={{ fontSize: '14px', color: theme.r4r.ink, mt: '16px' }}
          dangerouslySetInnerHTML={{ __html: post.text }}
        />
        {attachments}
      </BackNavigationContent>
    </>
  )
}

export default BoardsPost
